import { Activity, ArrowRight } from "lucide-react";
import { useMemo, type JSX } from "react";
import { Link } from "react-router";

import type { AnalyticsTrendPoint } from "../../lib/api/analytics.js";
import { summarizeGapObservations } from "../../lib/analytics/gap-observations.js";
import { Button } from "../ui/button.js";
import { InfoHint } from "./InfoHint.js";

interface Props {
  points: AnalyticsTrendPoint[];
  isLoading?: boolean;
  errorDetail?: string | null;
  emptyAction?: JSX.Element | null;
}

function utcTimestamp(value: string): string {
  return new Date(value).toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

/** Observed gaps are scan evidence, not SKU availability or lost sales. */
export function OperationsHealthCard({
  points,
  isLoading = false,
  errorDetail = null,
  emptyAction = null,
}: Props): JSX.Element {
  const stats = useMemo(() => summarizeGapObservations(points), [points]);

  return (
    <section className="flex flex-col overflow-hidden rounded-xl bg-card shadow-sm ring-1 ring-border/70" aria-label="Detected shelf gaps" aria-busy={isLoading}>
      <header className="flex items-center justify-between border-b border-border/60 px-5 py-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Detected shelf gaps</h2>
          <InfoHint
            text="Gap detections divided by product facings plus gap detections. This count-based signal does not measure empty shelf width, SKU availability or sales impact. Repeat scans may observe the same gap."
            label="How are detected gaps measured?"
          />
        </div>
        <span className="text-xs text-muted-foreground">last 30 days · UTC</span>
      </header>
      <div className="flex flex-1 flex-col gap-3 px-5 py-5">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading scan evidence…</p>
        ) : errorDetail !== null ? (
          <p className="text-sm text-danger">{errorDetail}</p>
        ) : stats.ratePct === null ? (
          <>
            <p className="text-sm text-muted-foreground">No measured gap ratio for this period.</p>
            <p className="text-xs text-muted-foreground">
              {stats.captures > 0
                ? `${stats.analyzedCaptures} of ${stats.captures} captures have a completed analysis; no product or gap detections were measured.`
                : "No current scan summary is available. Check scan history or rebuild the analytics summary."}
              {" "}Missing evidence does not mean the shelves are clear.
            </p>
            {emptyAction}
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-5xl font-bold tabular-nums text-foreground">{stats.gaps.toLocaleString()}</span>
              <span className="text-sm text-muted-foreground">gap detections</span>
            </div>
            <p className="text-sm text-foreground">{stats.ratePct.toFixed(1)}% of measured facings and gaps</p>
            <p className="text-xs text-muted-foreground">Use the worklist to inspect the evidence and decide what needs fixing. Repeated observations can refer to the same shelf gap.</p>
            <p className="text-xs text-muted-foreground">
              {stats.analyzedCaptures.toLocaleString()} of {stats.captures.toLocaleString()} captures analysed · {stats.facings.toLocaleString()} facings
            </p>
          </>
        )}
        {!isLoading && errorDetail === null && (stats.observedThrough || stats.computedAt) ? (
          <div className="text-xs text-muted-foreground">
            {stats.observedThrough ? <p>Latest included scan: {utcTimestamp(stats.observedThrough)}</p> : null}
            {stats.computedAt ? <p>Oldest summary calculation: {utcTimestamp(stats.computedAt)}</p> : null}
          </div>
        ) : null}
        <Button asChild size="sm" variant="ghost" className="mt-auto w-fit px-0 text-xs">
          <Link to="/analytics/trends">See the trend<ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
        </Button>
      </div>
    </section>
  );
}
